// Full-text + filtered search over people.
//
// Free text matches names (all visible names, nicknames), places, occupations, event
// labels, notes, tags, groups, gender, pronouns, and years; with prefix, typo-tolerant,
// and Soundex (sounds-like) matching for names. Filters use `key:value` syntax — see
// SEARCH_HELP for the list. Every free-text term must match somewhere (AND).

import { dateYear, dateYearRange } from './dates';
import { graphOf } from './graph';
import { birthDate, deathDate, displayName, EVENT_LABELS, GENDER_LABELS, genderLabel, isDeceased, isLiving, lifeStatus, primaryName, visibleNames } from './people';
import type { GenderIdentity, Id, Person, Tree } from './types';

export type FieldName = 'name' | 'place' | 'occupation' | 'event' | 'note' | 'tag' | 'group' | 'gender' | 'year';

const FIELD_WEIGHT: Record<FieldName, number> = {
  name: 3, place: 1.5, occupation: 1.5, event: 1, note: 0.7, tag: 2, group: 1.5, gender: 1, year: 2,
};

export const FIELD_LABEL: Record<FieldName, string> = {
  name: 'Name', place: 'Place', occupation: 'Occupation', event: 'Event', note: 'Notes', tag: 'Tag', group: 'Group',
  gender: 'Gender', year: 'Year',
};

interface Field {
  field: FieldName;
  text: string;
  norm: string;
  tokens: string[];
}

interface Doc {
  person: Person;
  fields: Field[];
  soundex: Set<string>;
  birth?: [number, number];
  death?: [number, number];
}

export function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

export function soundex(word: string): string {
  const w = normalize(word).replace(/[^a-z]/g, '');
  if (!w) return '';
  const codes: Record<string, string> = {
    b: '1', f: '1', p: '1', v: '1', c: '2', g: '2', j: '2', k: '2', q: '2', s: '2', x: '2', z: '2',
    d: '3', t: '3', l: '4', m: '5', n: '5', r: '6',
  };
  let out = w[0].toUpperCase();
  let last = codes[w[0]] ?? '';
  for (let i = 1; i < w.length && out.length < 4; i++) {
    const ch = w[i];
    const c = codes[ch] ?? '';
    if (c && c !== last) out += c;
    // h and w don't separate duplicate codes; vowels do.
    if (ch !== 'h' && ch !== 'w') last = c;
  }
  return out.padEnd(4, '0');
}

/** Damerau-Levenshtein distance with an early exit once it exceeds max. */
export function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev2 = new Array(b.length + 1).fill(0);
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
      cur.push(v);
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    for (let j = 0; j <= b.length; j++) prev2[j] = prev[j];
    prev = cur;
  }
  return prev[b.length];
}

const indexCache = new WeakMap<Tree, Doc[]>();

function buildIndex(tree: Tree): Doc[] {
  const hit = indexCache.get(tree);
  if (hit) return hit;
  const g = graphOf(tree);
  const docs: Doc[] = [];
  for (const p of Object.values(tree.people)) {
    const fields: Field[] = [];
    const add = (field: FieldName, text: string | undefined) => {
      if (!text?.trim()) return;
      const norm = normalize(text);
      fields.push({ field, text, norm, tokens: norm.split(' ').filter(Boolean) });
    };
    const soundexes = new Set<string>();
    for (const n of visibleNames(p)) {
      add('name', [n.prefix, n.given, n.nickname, n.surname, n.suffix].filter(Boolean).join(' '));
      // Soundex only for surnames: on short given names it matches far too much (Jane ≈ Jon).
      for (const w of n.surname.split(/\s+/)) {
        const s = soundex(w);
        if (s) soundexes.add(s);
      }
    }
    for (const e of p.events) {
      add('place', e.place);
      if (e.type === 'occupation') add('occupation', e.label);
      else add('event', [EVENT_LABELS[e.type], e.label].filter(Boolean).join(' '));
      add('note', e.note);
      const r = dateYearRange(e.date);
      if (r) add('year', String(r[0]) + (r[1] !== r[0] ? ` ${r[1]}` : ''));
    }
    add('note', p.notes);
    for (const t of p.tags) add('tag', t);
    for (const grp of g.groupsOf.get(p.id) ?? []) add('group', grp.name);
    if (p.gender.identity !== 'unknown') add('gender', genderLabel(p.gender));
    add('gender', p.pronouns);
    docs.push({ person: p, fields, soundex: soundexes, birth: dateYearRange(birthDate(p)), death: dateYearRange(deathDate(p)) });
  }
  indexCache.set(tree, docs);
  return docs;
}

// ---------------------------------------------------------------- query parsing

export interface Filter {
  key: string;
  value: string;
  negate: boolean;
}

export interface Query {
  terms: string[];
  phrases: string[];
  filters: Filter[];
}

export const FILTER_KEYS = [
  'born', 'died', 'place', 'birthplace', 'deathplace', 'name', 'given', 'surname', 'tag', 'group', 'gender',
  'pronouns', 'occupation', 'is', 'has', 'no', 'year',
] as const;

export function parseQuery(q: string): Query {
  const terms: string[] = [];
  const phrases: string[] = [];
  const filters: Filter[] = [];
  const re = /(-?)([a-z]+):("([^"]*)"|\S+)|"([^"]*)"|(\S+)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(q))) {
    if (m[2] && (FILTER_KEYS as readonly string[]).includes(m[2].toLowerCase())) {
      filters.push({ key: m[2].toLowerCase(), value: (m[4] ?? m[3]).trim(), negate: m[1] === '-' });
    } else if (m[5] !== undefined) {
      const p = normalize(m[5]);
      if (p) phrases.push(p);
    } else {
      const raw = m[0];
      for (const t of normalize(raw).split(' ')) if (t) terms.push(t);
    }
  }
  return { terms, phrases, filters };
}

function yearSpec(v: string): ((r: [number, number] | undefined) => boolean) | undefined {
  let m: RegExpMatchArray | null;
  if ((m = v.match(/^(\d{1,4})\s*(?:\.\.|-|to)\s*(\d{1,4})$/))) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    return (r) => !!r && r[1] >= a && r[0] <= b;
  }
  if ((m = v.match(/^<=?(\d{1,4})$/))) {
    const a = Number(m[1]);
    return (r) => !!r && r[0] <= a;
  }
  if ((m = v.match(/^>=?(\d{1,4})$/))) {
    const a = Number(m[1]);
    return (r) => !!r && r[1] >= a;
  }
  if ((m = v.match(/^(\d{3})0s$/))) {
    const a = Number(m[1] + '0');
    return (r) => !!r && r[1] >= a && r[0] <= a + 9;
  }
  if ((m = v.match(/^(\d{1,4})$/))) {
    const a = Number(m[1]);
    return (r) => !!r && r[0] <= a && r[1] >= a;
  }
  return undefined;
}

/** born:=1850s and born:=1850 match the birth year Reports counts, not every year a vague date could mean. */
function exactYear(v: string, year: number | undefined): boolean {
  if (year === undefined) return false;
  const m = v.match(/^(\d{3})0s$/);
  if (m) return Math.floor(year / 10) === Number(m[1]);
  return /^\d{1,4}$/.test(v) && year === Number(v);
}

function filterMatches(tree: Tree, d: Doc, f: Filter): boolean {
  const p = d.person;
  const g = graphOf(tree);
  // key:=value matches the whole value (ignoring case, accents, and punctuation), as Reports counts it.
  const exact = f.value.startsWith('=');
  const v = normalize(exact ? f.value.slice(1) : f.value);
  const fieldHas = (names: FieldName[]) => d.fields.some((x) => names.includes(x.field) && x.norm.includes(v));
  if (exact) {
    switch (f.key) {
      case 'born': return exactYear(f.value.slice(1).trim(), dateYear(birthDate(p)));
      case 'birthplace': return normalize(p.events.find((e) => e.type === 'birth')?.place ?? '') === v;
      case 'surname': return normalize(primaryName(p)?.surname ?? '') === v;
      case 'pronouns': return normalize(p.pronouns ?? '') === v;
      case 'occupation': return p.events.some((e) => e.type === 'occupation' && normalize(e.label ?? '') === v);
      case 'gender': {
        const id = (Object.keys(GENDER_LABELS) as GenderIdentity[]).find((k) => k !== 'custom' && normalize(k) === v);
        return (!!id && p.gender.identity === id) || (p.gender.identity === 'custom' && normalize(genderLabel(p.gender)) === v);
      }
    }
  }
  switch (f.key) {
    case 'born': return yearSpec(f.value)?.(d.birth) ?? false;
    case 'died': return yearSpec(f.value)?.(d.death) ?? false;
    case 'year': {
      const spec = yearSpec(f.value);
      return !!spec && p.events.some((e) => spec(dateYearRange(e.date)));
    }
    case 'place': return fieldHas(['place']);
    case 'birthplace': return normalize(p.events.find((e) => e.type === 'birth')?.place ?? '').includes(v);
    case 'deathplace': return normalize(p.events.find((e) => e.type === 'death' || e.type === 'burial')?.place ?? '').includes(v);
    case 'name': return fieldHas(['name']);
    case 'given': return visibleNames(p).some((n) => normalize(`${n.given} ${n.nickname ?? ''}`).split(' ').some((t) => t.startsWith(v)));
    case 'surname': {
      // surname:~smith matches by sound (Soundex); plain surname:smi matches by prefix.
      if (f.value.startsWith('~')) {
        const sx = soundex(f.value.slice(1));
        return visibleNames(p).some((n) => n.surname.split(/\s+/).some((w) => soundex(w) === sx));
      }
      return visibleNames(p).some((n) => normalize(n.surname).split(' ').some((t) => t.startsWith(v)));
    }
    case 'tag': return p.tags.some((t) => normalize(t) === v || normalize(t).startsWith(v));
    case 'group': return (g.groupsOf.get(p.id) ?? []).some((x) => normalize(x.name).includes(v));
    case 'gender': {
      const id = (Object.keys(GENDER_LABELS) as GenderIdentity[]).find((k) => normalize(k) === v);
      // Trans women are women and trans men are men.
      if (id === 'woman') return p.gender.identity === 'woman' || p.gender.identity === 'trans-woman';
      if (id === 'man') return p.gender.identity === 'man' || p.gender.identity === 'trans-man';
      if (id) return p.gender.identity === id;
      return normalize(genderLabel(p.gender)).includes(v);
    }
    case 'pronouns': return normalize(p.pronouns ?? '').includes(v);
    case 'occupation': return fieldHas(['occupation']);
    case 'is':
      if (v === 'living' || v === 'alive') return isLiving(p);
      if (v === 'deceased' || v === 'dead') return isDeceased(p);
      if (v === 'unknown' || v === 'not-recorded' || v === 'unrecorded') return lifeStatus(p) === 'unknown';
      if (v === 'home') return tree.homePersonId === p.id;
      return false;
    case 'has':
    case 'no': {
      const has = (() => {
        switch (v) {
          case 'parents': return (g.parentRels.get(p.id) ?? []).length > 0;
          case 'children': return (g.childRels.get(p.id) ?? []).length > 0;
          case 'partner': case 'partners': return (g.partnerRels.get(p.id) ?? []).length > 0;
          case 'friends': return (g.socialRels.get(p.id) ?? []).length > 0;
          case 'notes': return !!p.notes.trim();
          case 'birth': return p.events.some((e) => e.type === 'birth' && (e.date || e.place));
          case 'death': return p.events.some((e) => e.type === 'death');
          case 'pronouns': return !!normalize(p.pronouns ?? '');
          case 'tags': return p.tags.length > 0;
          case 'group': case 'groups': return (g.groupsOf.get(p.id) ?? []).length > 0;
          case 'relationships': return (['parentRels', 'childRels', 'partnerRels', 'siblingRels', 'socialRels'] as const).some((k) => (g[k].get(p.id) ?? []).length);
          default: return false;
        }
      })();
      return f.key === 'has' ? has : !has;
    }
  }
  return true;
}

// ---------------------------------------------------------------- matching

export interface Match {
  field: FieldName;
  text: string;
}

export interface SearchResult {
  person: Person;
  score: number;
  matches: Match[];
}

function termScore(term: string, token: string): number {
  if (token === term) return 1;
  if (token.startsWith(term)) return 0.8 - Math.min(0.3, (token.length - term.length) * 0.03);
  if (term.length >= 3 && token.includes(term)) return 0.5;
  if (term.length >= 4) {
    const max = term.length >= 7 ? 2 : 1;
    // Compare against the token's prefix too, so "smit" ≈ "smyth…" still works while typing.
    if (editDistance(term, token, max) <= max) return 0.45;
    if (token.length > term.length && editDistance(term, token.slice(0, term.length), 1) <= 1) return 0.35;
  }
  return 0;
}

export interface SearchOptions {
  limit?: number;
}

export function search(tree: Tree, q: string, opts: SearchOptions = {}): SearchResult[] {
  const docs = buildIndex(tree);
  const query = parseQuery(q);
  const out: SearchResult[] = [];
  const termSoundex = query.terms.map((t) => (t.length >= 3 && /^[a-z]/.test(t) ? soundex(t) : ''));

  for (const d of docs) {
    if (!query.filters.every((f) => (f.negate ? !filterMatches(tree, d, f) : filterMatches(tree, d, f)))) continue;
    let total = 0;
    const matches = new Map<string, Match>();
    let ok = true;
    for (let i = 0; i < query.terms.length; i++) {
      const term = query.terms[i];
      let best = 0;
      let bestField: Field | undefined;
      for (const f of d.fields) {
        for (const tok of f.tokens) {
          const s = termScore(term, tok) * FIELD_WEIGHT[f.field];
          if (s > best) {
            best = s;
            bestField = f;
          }
        }
      }
      if (!best && termSoundex[i] && d.soundex.has(termSoundex[i])) {
        best = 0.3 * FIELD_WEIGHT.name;
        bestField = d.fields.find((f) => f.field === 'name');
      }
      if (!best) {
        ok = false;
        break;
      }
      total += best;
      if (bestField) matches.set(bestField.field + bestField.text, { field: bestField.field, text: bestField.text });
    }
    if (!ok) continue;
    for (const ph of query.phrases) {
      const f = d.fields.find((x) => x.norm.includes(ph));
      if (!f) {
        ok = false;
        break;
      }
      total += 2 * FIELD_WEIGHT[f.field];
      matches.set(f.field + f.text, { field: f.field, text: f.text });
    }
    if (!ok) continue;
    // Bonus when the query reads like the start of the display name.
    if (query.terms.length) {
      const dn = normalize(displayName(d.person));
      if (dn.startsWith(query.terms.join(' '))) total += 2;
    }
    out.push({ person: d.person, score: total, matches: [...matches.values()] });
  }
  out.sort((a, b) => b.score - a.score || displayName(a.person).localeCompare(displayName(b.person)));
  return opts.limit ? out.slice(0, opts.limit) : out;
}

export const SEARCH_HELP: { syntax: string; meaning: string }[] = [
  { syntax: 'jane smith', meaning: 'Every word must match a name, place, note, tag, group, or year' },
  { syntax: '"new york"', meaning: 'Exact phrase' },
  { syntax: 'born:1850..1900', meaning: 'Born in a year range (also born:1850, born:<1900, born:1850s)' },
  { syntax: 'died:>1950', meaning: 'Died after 1950' },
  { syntax: 'place:boston', meaning: 'Any event place (also birthplace:, deathplace:)' },
  { syntax: 'surname:smith', meaning: 'Surname starts with (also given:, name:)' },
  { syntax: 'surname:~smith', meaning: 'Surname sounds like (Soundex): Smith, Smyth, Smythe…' },
  { syntax: 'gender:nonbinary', meaning: 'Any gender, e.g. woman (includes trans women), trans-man, genderfluid, or a self-described label' },
  { syntax: 'pronouns:they', meaning: 'Pronouns contain' },
  { syntax: 'tag:veteran  group:"book club"', meaning: 'Tag or group membership' },
  { syntax: 'occupation:teacher', meaning: 'Occupation contains' },
  { syntax: 'is:living  is:deceased  is:unknown', meaning: 'Living status (unknown: not recorded)' },
  { syntax: 'has:children  no:parents', meaning: 'has:/no: parents, children, partner, friends, notes, birth, death, pronouns, tags, groups' },
  { syntax: 'surname:=smith', meaning: 'Exactly, ignoring case (also birthplace:=, pronouns:=, occupation:=, gender:=, born:=1850s)' },
  { syntax: '-tag:draft', meaning: 'Put - before a filter to exclude matches' },
];

export function idsMatching(tree: Tree, q: string): Set<Id> {
  return new Set(search(tree, q).map((r) => r.person.id));
}
